// ---------------------------------------------------------------------------
// Typed HTTP client for the PDM assignment endpoints.
//
// Built on the shared `ApiService` (MSAL token acquisition + RFC 7807
// ProblemDetails → ApiError parsing). Talks to the real backend under `/api/pdm/*` and adapts the backend DTOs to the frontend model.
//
// Endpoints (PdmController):
//   GET    /api/pdm/assignments      → partner rows + grouped assignments
//   POST   /api/pdm/assignments      → create one (person, role) assignment
//   DELETE /api/pdm/assignments/{id} → remove one assignment (If-Match etag)
//   GET    /api/pdm/                 → PDM directory for the + Add picker
//   GET    /api/pdm/audit            → paged audit trail (newest first)
// ---------------------------------------------------------------------------

import type { ApiService } from './apiService';
import type { IUserDirectorySearch } from './graphDirectoryService';
import type {
  CreatePdmAssignmentRequest,
  PagedPdmAudit,
  PartnerAssignmentRow,
  PdmAssignment,
  PdmAuditQuery,
  PdmAuditRecord,
  PdmDirectoryEntry,
  RegionalLeadGroup,
} from '../models/pdmAssignment';
import type { PdmRole } from '../constants/pdmRoleLabels';

interface BackendPdmAssignmentDto {
  id: string;
  etag: string;
  name: string;
  email: string;
  role: PdmRole;
}

interface BackendPartnerAssignmentRow {
  mpnId: string;
  partnerName: string;
  tier: string | null;
  region: string | null;
  country: string | null;
  pdm: BackendPdmAssignmentDto[];
  pdmManager: BackendPdmAssignmentDto[];
  regionalLeads: BackendPdmAssignmentDto[];
}

interface BackendPdmAuditRecord {
  id: string;
  changedAt: string;
  action: 'assigned' | 'unassigned';
  mpnId: string;
  partnerName: string;
  region: string | null;
  fromPdmName: string | null;
  fromRole: string | null;
  toPdmName: string | null;
  toRole: string | null;
  changedBy: string;
}

interface BackendPdmAuditResponse {
  summary: { total: number; page: number; pageSize: number };
  records: BackendPdmAuditRecord[];
}

interface BackendPdmDirectoryEntry {
  name: string;
  email: string;
}

interface BackendRegionalLeadDto {
  id: string;
  etag: string;
  region: string;
  name: string;
  email: string;
}

interface BackendRegionalLeadGroup {
  region: string;
  leads: BackendRegionalLeadDto[];
}

const toRegionalLead = (dto: BackendRegionalLeadDto): PdmAssignment => ({
  id: dto.id,
  etag: dto.etag,
  pdmId: dto.email,
  name: dto.name,
  email: dto.email,
  role: 'regional_leads',
  region: dto.region,
});

const toRegionalLeadGroup = (dto: BackendRegionalLeadGroup): RegionalLeadGroup => ({
  region: dto.region,
  leads: (dto.leads ?? []).map(toRegionalLead),
});

const toAssignment = (dto: BackendPdmAssignmentDto): PdmAssignment => ({
  id: dto.id,
  etag: dto.etag,
  pdmId: dto.email,
  name: dto.name,
  email: dto.email,
  role: dto.role,
});

const toRow = (dto: BackendPartnerAssignmentRow): PartnerAssignmentRow => ({
  partnerId: dto.mpnId,
  locationId: dto.mpnId,
  partnerName: dto.partnerName,
  tier: dto.tier ?? '',
  region: dto.region ?? '',
  country: dto.country ?? '',
  pdm: dto.pdm.map(toAssignment),
  pdmManager: dto.pdmManager.map(toAssignment),
  regionalLeads: dto.regionalLeads.map(toAssignment),
});

const toAuditRecord = (dto: BackendPdmAuditRecord): PdmAuditRecord => ({
  id: dto.id,
  when: dto.changedAt,
  action: dto.action,
  partnerId: dto.mpnId,
  locationId: dto.mpnId,
  partnerName: dto.partnerName,
  region: dto.region ?? '',
  fromPdmName: dto.fromPdmName,
  fromPdmEmail: null,
  fromRole: dto.fromRole,
  toPdmName: dto.toPdmName,
  toPdmEmail: null,
  toRole: dto.toRole,
  actorName: dto.changedBy,
  actorEmail: '',
});

const toDirectoryEntry = (dto: BackendPdmDirectoryEntry): PdmDirectoryEntry => ({
  pdmId: dto.email,
  name: dto.name,
  email: dto.email,
});

/**
 * Contract implemented by the HTTP-backed PDM assignment service
 * (see `usePdmAssignmentService`).
 */
export interface IPdmAssignmentService {
  listAssignments(): Promise<PartnerAssignmentRow[]>;
  listAudit(query: PdmAuditQuery): Promise<PagedPdmAudit>;
  listPdmPool(search?: string): Promise<PdmDirectoryEntry[]>;
  addAssignment(request: CreatePdmAssignmentRequest): Promise<PdmAssignment>;
  removeAssignment(id: string, etag: string): Promise<void>;
  listRegionalLeads(): Promise<RegionalLeadGroup[]>;
  addRegionalLead(region: string, name: string, email: string): Promise<PdmAssignment>;
  removeRegionalLead(id: string, etag: string): Promise<void>;
}

export class PdmAssignmentService implements IPdmAssignmentService {
  constructor(
    private readonly api: ApiService,
    private readonly directorySearch: IUserDirectorySearch,
  ) {}

  /** Full Gold/Platinium partner set with assignments; all filtering happens client-side. */
  async listAssignments(): Promise<PartnerAssignmentRow[]> {
    const rows = await this.api.get<BackendPartnerAssignmentRow[]>('/pdm/assignments');
    return (rows ?? []).map(toRow);
  }

  /** The audit trail, newest first (FR-30), server-side paged and filtered. */
  async listAudit(query: PdmAuditQuery): Promise<PagedPdmAudit> {
    const params = new URLSearchParams({
      page: String(query.page),
      pageSize: String(query.pageSize),
    });
    if (query.search) params.set('search', query.search);

    const response = await this.api.get<BackendPdmAuditResponse>(`/pdm/audit?${params.toString()}`);
    return {
      records: (response?.records ?? []).map(toAuditRecord),
      total: response?.summary.total ?? 0,
      page: response?.summary.page ?? query.page,
      pageSize: response?.summary.pageSize ?? query.pageSize,
    };
  }

  /**
   * Directory of selectable PDMs for the + Add picker. With no `search` term this
   * returns the backend quick-select list. A search term runs a delegated
   * Microsoft Graph lookup directly from the frontend.
   */
  async listPdmPool(search?: string): Promise<PdmDirectoryEntry[]> {
    const term = search?.trim();
    if (term) {
      return this.directorySearch.searchUsers(term);
    }

    const pool = await this.api.get<BackendPdmDirectoryEntry[]>('/pdm/');
    return (pool ?? []).map(toDirectoryEntry);
  }

  /** Adds one (person, role) assignment. */
  async addAssignment(request: CreatePdmAssignmentRequest): Promise<PdmAssignment> {
    const dto = await this.api.post<BackendPdmAssignmentDto>('/pdm/assignments', {
      mpnId: request.partnerId,
      role: request.role,
      pdmName: request.pdmName,
      pdmEmail: request.pdmEmail,
    });
    return toAssignment(dto);
  }

  /**
   * Removes one assignment by its deterministic id, carrying the document's
   * ETag as `If-Match` for optimistic concurrency (FR-43).
   */
  async removeAssignment(id: string, etag: string): Promise<void> {
    await this.api.delete<void>(`/pdm/assignments/${encodeURIComponent(id)}`, {
      headers: etag ? { 'If-Match': etag } : undefined,
    });
  }

  /** Regional leads grouped by region, for the "Edit regional lead" panel. */
  async listRegionalLeads(): Promise<RegionalLeadGroup[]> {
    const groups = await this.api.get<BackendRegionalLeadGroup[]>('/pdm/regional-leads');
    return (groups ?? []).map(toRegionalLeadGroup);
  }

  /** Assigns one person as a regional lead for a whole region. */
  async addRegionalLead(region: string, name: string, email: string): Promise<PdmAssignment> {
    const dto = await this.api.post<BackendRegionalLeadDto>('/pdm/regional-leads', {
      region,
      name,
      email,
    });
    return toRegionalLead(dto);
  }

  /** Removes one regional lead by its deterministic id (If-Match etag). */
  async removeRegionalLead(id: string, etag: string): Promise<void> {
    await this.api.delete<void>(`/pdm/regional-leads/${encodeURIComponent(id)}`, {
      headers: etag ? { 'If-Match': etag } : undefined,
    });
  }
}
